
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useWarranty } from './hooks/useWarranty';
import { ReplacementOrder } from './types';

export default function ReplacementOrders() {
  const { replacements, claims, addReplacement, updateReplacement } = useWarranty();
  const { register, handleSubmit, reset } = useForm();
  const [showForm, setShowForm] = useState(false);
  const [selectedReplacement, setSelectedReplacement] = useState<ReplacementOrder | null>(null);

  const onSubmit = async (data: any) => {
    try {
      await addReplacement.mutateAsync({
        ...data,
        id: `REP${Date.now()}`,
        status: 'initiated',
        reason: data.reason || 'Product replacement required',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      reset();
      setShowForm(false);
    } catch (error) {
      console.error('Error adding replacement order:', error);
    }
  };

  const updateReplacementStatus = async (id: string, status: string) => {
    try {
      await updateReplacement.mutateAsync({
        id,
        data: { status, updatedAt: new Date().toISOString() }
      });
    } catch (error) {
      console.error('Error updating replacement:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'initiated': return 'bg-yellow-100 text-yellow-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (replacements.isLoading) return <div className="p-6">Loading replacement orders...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Replacement Orders</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-[var(--color-primary)] text-white px-4 py-2 rounded-lg hover:bg-[var(--color-primary)]/90 transition-colors"
        >
          New Replacement Order
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <h3 className="text-sm font-medium text-gray-600">Initiated</h3>
          <p className="text-2xl font-bold text-yellow-600">
            {replacements.data?.filter((r: ReplacementOrder) => r.status === 'initiated').length || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <h3 className="text-sm font-medium text-gray-600">Delivered</h3>
          <p className="text-2xl font-bold text-green-600">
            {replacements.data?.filter((r: ReplacementOrder) => r.status === 'delivered').length || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-gray-500">
          <h3 className="text-sm font-medium text-gray-600">Closed</h3>
          <p className="text-2xl font-bold text-gray-600">
            {replacements.data?.filter((r: ReplacementOrder) => r.status === 'closed').length || 0}
          </p>
        </div>
      </div>

      {/* Simple Form */}
      {showForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="grid md:grid-cols-3 gap-4 bg-white p-4 rounded shadow">
          <select
            {...register('claimId', { required: true })}
            className="input"
          >
            <option value="">Select Claim ID</option>
            {claims.data?.filter((c: any) => c.status === 'approved').map((claim: any) => (
              <option key={claim.id} value={claim.id}>
                {claim.id} - {claim.customer}
              </option>
            ))}
          </select>
          <input className="input" placeholder="Old Product" {...register('oldProduct', { required: true })} />
          <input className="input" placeholder="New Product" {...register('newProduct', { required: true })} />
          <input className="input" type="date" {...register('dateIssued', { required: true })} />
          <input className="input" placeholder="Approved By" {...register('approvedBy', { required: true })} />
          <div className="flex space-x-2">
            <button type="submit" className="btn-primary flex-1">Add Replacement</button>
            <button type="button" onClick={() => setShowForm(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400">Cancel</button>
          </div>
        </form>
      )}

      {/* Replacement Records Table */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-semibold mb-2">Replacement Records</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-accent)]/20">
              <tr>
                <th className="th">Claim ID</th>
                <th className="th">Old</th>
                <th className="th">New</th>
                <th className="th">Status</th>
                <th className="th">Date</th>
                <th className="th">Approved By</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {replacements.data?.map((r: ReplacementOrder) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="td">{r.claimId}</td>
                  <td className="td">{r.oldProduct}</td>
                  <td className="td">{r.newProduct}</td>
                  <td className="td">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="td">{new Date(r.dateIssued).toLocaleDateString()}</td>
                  <td className="td">{r.approvedBy}</td>
                  <td className="td space-x-2">
                    {r.status === 'initiated' && (
                      <button
                        onClick={() => updateReplacementStatus(r.id, 'delivered')}
                        className="text-green-600 hover:text-green-900 text-xs"
                      >
                        Mark Delivered
                      </button>
                    )}
                    {r.status === 'delivered' && (
                      <button
                        onClick={() => updateReplacementStatus(r.id, 'closed')}
                        className="text-gray-600 hover:text-gray-900 text-xs"
                      >
                        Close
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedReplacement(r)}
                      className="text-[var(--color-primary)] hover:text-[var(--color-primary)]/80 text-xs"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Replacement Detail Modal */}
      {selectedReplacement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Replacement Order Details</h2>
              <button
                onClick={() => setSelectedReplacement(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Replacement ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedReplacement.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Claim ID</label>
                <p className="mt-1 text-sm text-gray-900">{selectedReplacement.claimId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Old Product</label>
                <p className="mt-1 text-sm text-gray-900">{selectedReplacement.oldProduct}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">New Product</label>
                <p className="mt-1 text-sm text-gray-900">{selectedReplacement.newProduct}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date Issued</label>
                <p className="mt-1 text-sm text-gray-900">{new Date(selectedReplacement.dateIssued).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Approved By</label>
                <p className="mt-1 text-sm text-gray-900">{selectedReplacement.approvedBy}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">Reason</label>
                <p className="mt-1 text-sm text-gray-900">{selectedReplacement.reason}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`mt-1 inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedReplacement.status)}`}>
                  {selectedReplacement.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
